import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { companyInfo, submitContactForm } from '../utils/mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitContactForm(formData);
      
      toast({
        title: "Quote Request Submitted!",
        description: result.message,
        duration: 5000,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        serviceType: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-gradient-to-br from-[#FAF9F7] via-[#FFF8F0] to-white relative overflow-hidden">
      {/* Elegant Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(230, 160, 92, 0.1) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      
      {/* Background Decoration */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-[#E6A05C] rounded-full filter blur-3xl opacity-5"></div>
      <div className="absolute bottom-1/4 left-0 w-[500px] h-[500px] bg-[#D4894B] rounded-full filter blur-3xl opacity-5"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#E6A05C]/10 rounded-full px-6 py-2 mb-6">
            <Send className="text-[#E6A05C]" size={16} />
            <span className="text-[#E6A05C] font-semibold text-sm uppercase tracking-wide">Get In Touch</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0D2436] mb-6">
            Request Your Free Quote
          </h2>
          <p className="text-lg text-gray-600">
            Ready to move your cargo? Fill out the form and our team will get back to you within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
          {/* Contact Info Cards */}
          <div className="lg:col-span-2 space-y-6">
            {/* Email Card */}
            <div className="bg-[#F5E6D3] rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:scale-105 group">
              <div className="w-14 h-14 bg-gradient-to-br from-[#E6A05C] to-[#D4894B] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Mail className="text-[#F5E6D3]" size={24} />
              </div>
              <h3 className="text-lg font-bold text-[#0D2436] mb-2">Email Us</h3>
              <a href={`mailto:${companyInfo.email}`} className="text-[#E6A05C] hover:text-[#D4894B] transition-colors duration-200 break-all">
                {companyInfo.email}
              </a>
            </div>

            {/* Phone Card */}
            <div className="bg-[#F5E6D3] rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:scale-105 group">
              <div className="w-14 h-14 bg-gradient-to-br from-[#E6A05C] to-[#D4894B] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Phone className="text-[#F5E6D3]" size={24} />
              </div>
              <h3 className="text-lg font-bold text-[#0D2436] mb-2">Call Us</h3>
              <a href={`tel:${companyInfo.phoneTel}`} className="text-[#E6A05C] hover:text-[#D4894B] transition-colors duration-200">
                {companyInfo.phone}
              </a>
              <p className="text-sm text-gray-600 mt-2">Available 24/7</p>
            </div>

            {/* Address Card */}
            <div className="bg-[#F5E6D3] rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:scale-105 group">
              <div className="w-14 h-14 bg-gradient-to-br from-[#E6A05C] to-[#D4894B] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <MapPin className="text-[#F5E6D3]" size={24} />
              </div>
              <h3 className="text-lg font-bold text-[#0D2436] mb-2">Visit Us</h3>
              <p className="text-gray-600">{companyInfo.address}</p>
            </div>

            {/* Trust Badge */}
            <div className="bg-gradient-to-br from-[#0D2436] to-[#1B3A52] rounded-2xl p-6 text-[#F5E6D3]">
              <CheckCircle className="text-[#E6A05C] mb-4" size={32} />
              <h3 className="text-xl font-bold mb-2">Trusted by Hundreds</h3>
              <p className="text-[#F5E6D3]/80">
                Join 10+ businesses that trust us with their logistics needs.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-[#F5E6D3] rounded-2xl p-8 md:p-10 shadow-xl border border-gray-100">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#0D2436] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E6A05C] focus:border-transparent transition-all duration-200"
                    placeholder="John Doe"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#0D2436] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E6A05C] focus:border-transparent transition-all duration-200"
                    placeholder="john@company.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#0D2436] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E6A05C] focus:border-transparent transition-all duration-200"
                    placeholder="(905) 000-0000"
                  />
                </div>

                {/* Company */}
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-[#0D2436] mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E6A05C] focus:border-transparent transition-all duration-200"
                    placeholder="Your Company"
                  />
                </div>
              </div>

              {/* Service Type */}
              <div className="mb-6">
                <label htmlFor="serviceType" className="block text-sm font-semibold text-[#0D2436] mb-2">
                  Service Needed *
                </label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E6A05C] focus:border-transparent transition-all duration-200 bg-[#F5E6D3]"
                >
                  <option value="">Select a service</option>
                  <option value="freight">Freight & Long Haul</option>
                  <option value="logistics">Logistics & Warehousing</option>
                  <option value="crossborder">Cross Border US-Canada</option>
                  <option value="ftl">FTL (Full Truckload)</option>
                  <option value="ltl">LTL (Less Than Truckload)</option>
                  <option value="reefer">Reefer Services</option>
                </select>
              </div>

              {/* Message */}
              <div className="mb-8">
                <label htmlFor="message" className="block text-sm font-semibold text-[#0D2436] mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E6A05C] focus:border-transparent transition-all duration-200 resize-none"
                  placeholder="Tell us about your shipping needs..."
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#E6A05C] text-[#F5E6D3] py-4 rounded-lg font-semibold text-lg hover:bg-[#D4894B] transition-all duration-300 hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    <span>Request Quote</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
